import React, { useEffect, useRef, useState } from "react";
import "./SparkleHoverButton.css";

const SparkleIcon = () => (
  <svg
    viewBox="0 0 784.11 815.53"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="w-full h-auto fill-golden"
  >
    <path d="M392.05 0c-20.9,210.08 -184.06,378.41 -392.05,407.78 207.96,29.37 371.12,197.68 392.05,407.74 20.93,-210.06 184.09,-378.37 392.05,-407.74 -207.98,-29.38 -371.16,-197.69 -392.06,-407.78z" />
  </svg>
);

const SparkleStars = () => (
  <>
    <span className="sparkle-hover__star sparkle-hover__star--1">
      <SparkleIcon />
    </span>
    <span className="sparkle-hover__star sparkle-hover__star--2">
      <SparkleIcon />
    </span>
    <span className="sparkle-hover__star sparkle-hover__star--3">
      <SparkleIcon />
    </span>
    <span className="sparkle-hover__star sparkle-hover__star--4">
      <SparkleIcon />
    </span>
    <span className="sparkle-hover__star sparkle-hover__star--5">
      <SparkleIcon />
    </span>
    <span className="sparkle-hover__star sparkle-hover__star--6">
      <SparkleIcon />
    </span>
  </>
);

interface SparkleHoverButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  contentClassName?: string;
  triggerOnClick?: boolean;
  clickEffectMs?: number;
}

export const SparkleHoverButton = ({
  children,
  className = "",
  contentClassName = "",
  type = "button",
  triggerOnClick = false,
  clickEffectMs = 380,
  onPointerUp,
  onTouchEnd,
  onMouseUp,
  onClick,
  ...rest
}: SparkleHoverButtonProps) => {
  const [isClickActive, setIsClickActive] = useState(false);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearClickTimer = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
  };

  const startClickEffect = () => {
    if (!triggerOnClick) return;
    clearClickTimer();
    setIsClickActive(true);
    clickTimerRef.current = setTimeout(() => {
      setIsClickActive(false);
      clickTimerRef.current = null;
    }, clickEffectMs);
  };

  useEffect(() => {
    return () => {
      clearClickTimer();
    };
  }, []);

  const releaseFocus = (element: EventTarget | null) => {
    if (element instanceof HTMLButtonElement) {
      element.blur();
    }
  };

  return (
    <button
      type={type}
      className={`sparkle-hover ${
        triggerOnClick ? "sparkle-hover--click-trigger" : ""
      } ${isClickActive ? "sparkle-hover--click-active" : ""} ${className}`.trim()}
      onClick={(event) => {
        startClickEffect();
        onClick?.(event);
      }}
      onPointerUp={(event) => {
        onPointerUp?.(event);
        releaseFocus(event.currentTarget);
      }}
      onTouchEnd={(event) => {
        onTouchEnd?.(event);
        releaseFocus(event.currentTarget);
      }}
      onMouseUp={(event) => {
        onMouseUp?.(event);
        releaseFocus(event.currentTarget);
      }}
      {...rest}
    >
      <span className={`sparkle-hover__content ${contentClassName}`.trim()}>
        {children}
      </span>
      <SparkleStars />
    </button>
  );
};

interface SparkleHoverBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  contentClassName?: string;
}

export const SparkleHoverBadge = ({
  children,
  className = "",
  contentClassName = "",
  ...rest
}: SparkleHoverBadgeProps) => {
  return (
    <div className={`sparkle-hover ${className}`.trim()} {...rest}>
      <span className={`sparkle-hover__content ${contentClassName}`.trim()}>
        {children}
      </span>
      <SparkleStars />
    </div>
  );
};
